import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../components/AppIcon';
import useTranslation from '../../../hooks/useTranslation';
import useEscapeKey from '../../../hooks/useEscapeKey';
import Badge from '../../../components/ui/Badge';
import SearchableSelect from '../../../components/ui/SearchableSelect';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import { statusVariant } from '../../../utils/statusVariant';
import { formatCurrency } from '../../../utils/formatCurrency';
import { extraDirectiveNumber } from '../../../utils/extraDirectiveNumber';
import { divisionLabel } from '../../../constants/divisions';

/**
 * Fiche d'avenant — UNE modale, deux modes (meme principe que DirectiveModal) :
 *   view  consultation (clic sur la ligne, « Voir le detail »)
 *   edit  modification (« Modifier » en bas, ou depuis le menu ⋮)
 *
 * Modifiables : titre (colonne description), description (colonne notes),
 * n° d'avenant, montant ; sous-traitant seulement tant que l'avenant n'est ni
 * envoye ni signe.
 * Verrouilles, jamais reecrits : extra source, n° de directive, categorie,
 * division — le lien avenant <-> extra <-> directive (« Herite de l'extra
 * source »). Le statut se change par la pastille du tableau.
 */

const FIELD_H = 'h-9';
const SELECT_H = '[&_input]:h-9';

const SectionTitle = ({ children }) => (
  <div className="flex items-center gap-2 mb-1.5">
    <span className="w-[3px] h-3.5 rounded-full bg-primary flex-shrink-0" />
    <span className="text-[10px] font-semibold text-primary uppercase tracking-[0.12em]">{children}</span>
  </div>
);

// `locked` : champ herite affiche en modification (cadenas, jamais editable).
const ReadValue = ({ value, className = '', locked = false }) => (
  <div className={`${FIELD_H} flex items-center gap-1.5 px-3 text-xs rounded-lg border border-gray-100 bg-gray-50 min-w-0 ${className}`}>
    {locked && <Icon name="Lock" size={11} className="text-gray-400 flex-shrink-0" />}
    {value != null && value !== ''
      ? <span className="text-gray-800 truncate" title={locked ? undefined : String(value)}>{value}</span>
      : <span className="text-gray-300">—</span>}
  </div>
);

// Montant saisi a la quebecoise (« 1 234,56 ») ; la valeur reste un nombre.
const parseAmount = (raw) => {
  const cleaned = String(raw ?? '')?.replace(/[\s  $]/g, '')?.replace(',', '.');
  const n = parseFloat(cleaned);
  return Number.isNaN(n) ? null : n;
};

const formOf = (avenant) => ({
  subcontractId: avenant?.subcontractId || avenant?.subcontract?.id || '',
  title: avenant?.description || '',
  coNumber: avenant?.coNumber || '',
  notes: avenant?.notes || '',
  amount: avenant?.amount != null ? String(avenant?.amount)?.replace('.', ',') : '',
});

// Un avenant envoye ou signe engage deja le sous-traitant : on ne le change plus.
const SUBCONTRACT_LOCKED_STATUSES = ['sent', 'signed'];

const AvenantModal = ({ avenant: initialAvenant, mode: initialMode = 'view', subcontracts = [], projectId, onClose, onSave }) => {
  const { t } = useTranslation();
  const [avenant, setAvenant] = useState(initialAvenant);
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState(() => formOf(initialAvenant));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [confirmDiscard, setConfirmDiscard] = useState(null); // 'view' | 'close'

  useEffect(() => {
    setAvenant(initialAvenant);
    setForm(formOf(initialAvenant));
    setMode(initialMode);
    setErrors({});
    setSaveError(null);
  }, [initialAvenant, initialMode]);

  const isEditing = mode === 'edit';
  const initialForm = useMemo(() => formOf(avenant), [avenant]);
  const dirty = JSON.stringify(form) !== JSON.stringify(initialForm);

  const cancelEdit = () => {
    setForm(initialForm);
    setErrors({});
    setSaveError(null);
    setConfirmDiscard(null);
    setMode('view');
  };

  const requestClose = () => {
    if (isEditing && dirty) setConfirmDiscard('close');
    else onClose?.();
  };

  // Echap : en modification, retour a la consultation (confirmation si modifie).
  useEscapeKey(() => {
    if (isEditing) {
      if (dirty) setConfirmDiscard('view');
      else cancelEdit();
    } else onClose?.();
  }, !saving && !confirmDiscard);

  const subcontractLocked = SUBCONTRACT_LOCKED_STATUSES?.includes(avenant?.status);
  const subcontract = isEditing && !subcontractLocked
    ? subcontracts?.find((s) => s?.id === form?.subcontractId) || null
    : null;
  const subcontractorName = subcontract
    ? subcontract?.subcontractor?.companyName || null
    : avenant?.subcontract?.subcontractor?.companyName || avenant?.subcontractorName || null;

  // Le lien avec l'extra source ne se modifie jamais ici.
  const sourceExtra = avenant?.sourceExtra || null;
  const inheritedTitle = t('avenants.detail.inherited');

  const categoryLabel = (cat) => {
    if (!cat) return null;
    const key = `extras.category.${cat}`;
    const label = t(key);
    return label === key ? cat : label;
  };

  const amountHT = isEditing ? (parseAmount(form?.amount) || 0) : (parseFloat(avenant?.amount) || 0);
  const tps = amountHT * 0.05;
  const tvq = amountHT * 0.09975;
  const totalTTC = amountHT * 1.14975;
  const status = avenant?.status || 'draft';

  const validate = () => {
    const next = {};
    if (!subcontractLocked && !form?.subcontractId) next.subcontractId = t('avenants.detail.subcontractRequired');
    if (parseAmount(form?.amount) == null) next.amount = t('avenants.detail.amountRequired');
    setErrors(next);
    return Object.keys(next)?.length === 0;
  };

  const handleSave = async () => {
    if (saving || !validate()) return;
    setSaving(true);
    setSaveError(null);
    try {
      // Ni sourceExtraId ni rien qui touche au lien extra / directive : ces
      // colonnes restent intactes. Le sous-contrat n'est envoye que s'il est
      // modifiable.
      const payload = {
        projectId,
        description: form?.title?.trim() || null,
        coNumber: form?.coNumber?.trim() || null,
        notes: form?.notes?.trim() || null,
        amount: parseAmount(form?.amount) || 0,
      };
      if (!subcontractLocked) payload.subcontractId = form?.subcontractId;
      const updated = await onSave?.(avenant?.id, payload);
      if (updated) setAvenant(updated);
      setMode('view');
    } catch (err) {
      console.error('[avenant] enregistrement impossible:', err);
      setSaveError(t('avenants.detail.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const label = (text, required = false) => (
    <label className="block text-[11px] font-medium text-[#FF6B35] mb-1">
      {text} {required && isEditing && <span className="text-red-500">*</span>}
    </label>
  );
  const fieldError = (key) => (errors?.[key] ? <p className="mt-1 text-xs text-red-500">{errors?.[key]}</p> : null);
  const inputClass = (key) =>
    `w-full ${FIELD_H} px-3 py-2 text-xs leading-[18px] border rounded-lg outline-none transition-colors placeholder:text-gray-400 ${
      errors?.[key] ? 'border-red-300 bg-red-50' : 'border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20'
    }`;

  if (!avenant) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={(e) => { if (!isEditing && e?.target === e?.currentTarget) onClose?.(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col max-h-[90vh] overflow-hidden" role="dialog" aria-modal="true">
        {/* En-tete */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-violet-50 flex items-center justify-center">
              <Icon name="GitBranch" size={18} className="text-violet-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-gray-900 leading-snug truncate">
                {t('avenants.detail.eyebrow')} · {avenant?.coNumber || '—'}
              </h2>
              <p className="text-[11px] text-gray-500 truncate">{subcontractorName || '—'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {isEditing && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-50 text-violet-600">
                {t('avenants.detail.editMode')}
              </span>
            )}
            <Badge variant={statusVariant('changeOrder', status)}>
              {t(`avenants.detail.statusLabels.${status}`)}
            </Badge>
            <button
              type="button"
              onClick={requestClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        </div>

        {/* Corps */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {saveError && (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
              <Icon name="AlertCircle" size={15} />{saveError}
            </div>
          )}

          <div className="grid grid-cols-1 min-[900px]:grid-cols-12 gap-x-5 gap-y-3">
            {/* Gauche : rattachements */}
            <div className="min-[900px]:col-span-5 space-y-3">
              <SectionTitle>{t('avenants.detail.infoSection')}</SectionTitle>

              {/* Sous-traitant (sous-contrat) */}
              <div>
                {label(t('avenants.detail.subcontractor'), true)}
                {isEditing && !subcontractLocked ? (
                  <>
                    <div className={SELECT_H}>
                      <SearchableSelect
                        options={subcontracts?.map((s) => ({ value: s?.id, label: s?.subcontractor?.companyName || '—' }))}
                        value={form?.subcontractId || null}
                        onChange={(val) => { setForm((p) => ({ ...p, subcontractId: val || '' })); setErrors((p) => ({ ...p, subcontractId: undefined })); }}
                        placeholder={t('avenants.detail.subcontractPlaceholder')}
                      />
                    </div>
                    {fieldError('subcontractId')}
                  </>
                ) : (
                  <>
                    <ReadValue value={subcontractorName} />
                    {isEditing && subcontractLocked && (
                      <p className="mt-0.5 text-[10px] text-gray-400">{t('avenants.detail.subcontractLocked')}</p>
                    )}
                  </>
                )}
              </div>

              {/* N° d'avenant */}
              <div>
                {label(t('avenants.detail.coNumber'))}
                {isEditing ? (
                  <input
                    type="text"
                    value={form?.coNumber}
                    onChange={(e) => setForm((p) => ({ ...p, coNumber: e?.target?.value }))}
                    placeholder="09250.AV1"
                    className={inputClass('coNumber')}
                  />
                ) : (
                  <ReadValue value={avenant?.coNumber} />
                )}
              </div>

              {/* Herites de l'extra source — toujours en lecture */}
              <div title={isEditing ? inheritedTitle : undefined}>
                {label(t('avenants.detail.sourceExtra'))}
                <ReadValue value={sourceExtra?.title} locked={isEditing} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div title={isEditing ? inheritedTitle : undefined}>
                  {label(t('avenants.detail.directiveNumber'))}
                  <ReadValue value={extraDirectiveNumber(sourceExtra)} locked={isEditing} />
                </div>
                <div title={isEditing ? inheritedTitle : undefined}>
                  {label(t('avenants.detail.category'))}
                  <ReadValue value={categoryLabel(sourceExtra?.category)} locked={isEditing} />
                </div>
              </div>
              <div title={isEditing ? inheritedTitle : undefined}>
                {label(t('avenants.detail.division'))}
                <ReadValue value={sourceExtra?.division ? divisionLabel(t, sourceExtra?.division) : null} locked={isEditing} />
              </div>
              {isEditing && (
                <p className="text-[10px] text-gray-400 flex items-center gap-1">
                  <Icon name="Lock" size={10} />{inheritedTitle}
                </p>
              )}
            </div>

            {/* Droite : contenu et montant */}
            <div className="min-[900px]:col-span-7 space-y-3">
              <SectionTitle>{t('avenants.detail.contentSection')}</SectionTitle>

              {/* Titre de l'avenant (colonne description) */}
              <div>
                {label(t('avenants.detail.avenantTitle'))}
                {isEditing ? (
                  <input
                    type="text"
                    value={form?.title}
                    onChange={(e) => setForm((p) => ({ ...p, title: e?.target?.value }))}
                    className={inputClass('title')}
                  />
                ) : (
                  <ReadValue value={avenant?.description} />
                )}
              </div>

              {/* Description — saisie par le charge de projet (colonne notes) */}
              <div>
                {label(t('avenants.detail.description'))}
                {isEditing ? (
                  <textarea
                    rows={6}
                    value={form?.notes}
                    onChange={(e) => setForm((p) => ({ ...p, notes: e?.target?.value }))}
                    placeholder={t('avenants.detail.descriptionPlaceholder')}
                    className="w-full px-3 py-2 text-xs !leading-relaxed border border-gray-200 rounded-lg outline-none resize-y min-h-[9rem] focus:border-primary focus:ring-2 focus:ring-primary/20 placeholder:text-gray-400"
                  />
                ) : (
                  <div className="min-h-[9rem] px-3 py-2 text-xs leading-relaxed rounded-lg border border-gray-100 bg-gray-50 whitespace-pre-wrap break-words">
                    {avenant?.notes ? <span className="text-gray-800">{avenant?.notes}</span> : <span className="text-gray-300">—</span>}
                  </div>
                )}
              </div>

              {/* Montant */}
              <div>
                <SectionTitle>{t('avenants.detail.amountSection')}</SectionTitle>
                <div className="rounded-xl border border-gray-100 bg-gray-50/60 px-4 py-3 space-y-1.5">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs text-gray-500">
                      {t('avenants.detail.subtotal')} {isEditing && <span className="text-red-500">*</span>}
                    </span>
                    {isEditing ? (
                      <div className="relative w-40">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={form?.amount}
                          onChange={(e) => { setForm((p) => ({ ...p, amount: e?.target?.value?.replace(/[^0-9,.\-\s]/g, '') })); setErrors((p) => ({ ...p, amount: undefined })); }}
                          placeholder="0,00"
                          className={`${inputClass('amount')} pr-6 text-right tabular-nums bg-white`}
                        />
                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">$</span>
                      </div>
                    ) : (
                      <span className="text-xs font-semibold text-gray-800 tabular-nums">{formatCurrency(amountHT)}</span>
                    )}
                  </div>
                  {fieldError('amount')}
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-gray-400">{t('avenants.detail.tps')}</span>
                    <span className="text-[11px] text-gray-500 tabular-nums">{formatCurrency(tps)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-gray-400">{t('avenants.detail.tvq')}</span>
                    <span className="text-[11px] text-gray-500 tabular-nums">{formatCurrency(tvq)}</span>
                  </div>
                  <div className="flex items-center justify-between pt-2 mt-1 border-t border-gray-200">
                    <span className="text-[13px] font-bold text-gray-900">{t('avenants.detail.total')}</span>
                    <span className="text-[13px] font-bold text-gray-900 tabular-nums">{formatCurrency(totalTTC)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Pied */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 flex-shrink-0">
          {!isEditing ? (
            <>
              <button
                type="button"
                onClick={onClose}
                className="px-3.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('avenants.detail.close')}
              </button>
              <button
                type="button"
                onClick={() => setMode('edit')}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors"
              >
                <Icon name="Pencil" size={13} />
                {t('common.edit')}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => (dirty ? setConfirmDiscard('view') : cancelEdit())}
                disabled={saving}
                className="px-3.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-60"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-60"
              >
                {saving ? <Icon name="Loader2" size={13} className="animate-spin" /> : <Icon name="Check" size={13} />}
                {saving ? t('avenants.detail.saving') : t('avenants.detail.save')}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Abandonner les modifications ? */}
      <ConfirmDialog
        isOpen={!!confirmDiscard}
        title={t('avenants.detail.discardTitle')}
        message={t('avenants.detail.discardMessage')}
        confirmVariant="danger"
        icon="AlertTriangle"
        confirmLabel={t('avenants.detail.discardConfirm')}
        cancelLabel={t('avenants.detail.keepEditing')}
        onConfirm={() => (confirmDiscard === 'close' ? onClose?.() : cancelEdit())}
        onCancel={() => setConfirmDiscard(null)}
      />
    </div>,
    document.body
  );
};

export default AvenantModal;
